import { useTranslation } from 'react-i18next';
import { CoverageHistory } from 'src/domain/analytics';
import { AnalyticsChartFrame } from './AnalyticsCard';
import { useScopedAnalyticsText } from './analyticsScope';
import { COVERAGE_STATUSES as statuses } from './coverageStatuses';

export const CoverageHistoryChart = ({
    data,
    loading,
    error,
    embedded,
    population = 'residents',
}: {
    data?: CoverageHistory;
    loading: boolean;
    error?: unknown;
    embedded?: boolean;
    population?: 'residents' | 'employees';
}) => {
    const { t, i18n } = useTranslation();
    const text = useScopedAnalyticsText();
    const number = (value: number) => value.toLocaleString(i18n.language);
    const monthName = (month: string, year = false) =>
        new Date(`${month}-15T12:00:00Z`).toLocaleDateString(i18n.language, {
            month: 'short',
            ...(year ? { year: 'numeric' } : {}),
        });
    const employee = population === 'employees';
    const months = employee
        ? (data?.employeeMonths ?? []).map(row => ({
              ...row,
              counts: row.counts ? { ...row.counts, noLinkedContact: 0 } : null,
          }))
        : data?.months ?? [];
    const visibleStatuses = statuses.filter(
        status => !employee || status.key !== 'noLinkedContact',
    );
    const ceiling =
        Math.max(
            1,
            Math.ceil(
                Math.max(
                    0,
                    ...months.map(row => (row.counts ? row.total ?? 0 : 0)),
                ) / 4,
            ),
        ) * 4;
    const left = 38;
    const top = 26;
    const height = 160;
    const width = 540;
    const slot = width / Math.max(1, months.length);
    return (
        <AnalyticsChartFrame
            embedded={embedded}
            info={text(
                employee
                    ? 'admin.analytics.employeeHistoryNote'
                    : 'admin.analytics.coverageHistoryNote',
            )}
            title={t(
                employee
                    ? 'admin.analytics.employeeCoverageTitle'
                    : 'admin.analytics.coverageHistoryTitle',
            )}
            description={t(
                employee
                    ? 'admin.analytics.employeeHistoryScope'
                    : 'admin.analytics.coverageHistoryScope',
            )}
            className="analytics-coverage-history"
        >
            {error ? (
                <p role="alert" className="analytics-note">
                    {t('admin.analytics.coverageHistoryError')}
                </p>
            ) : !data ? (
                <p className="analytics-note">
                    {t(
                        loading
                            ? 'admin.analytics.loading'
                            : 'admin.analytics.coverageHistoryEmpty',
                    )}
                </p>
            ) : (
                <>
                    {!months.some(row => row.counts) && (
                        <p className="analytics-note">
                            {t('admin.analytics.coverageHistoryEmpty')}
                        </p>
                    )}
                    <div className="analytics-chart-legend" aria-hidden="true">
                        <span>
                            {months.length
                                ? `${monthName(
                                      months[0].month,
                                      true,
                                  )} – ${monthName(
                                      months[months.length - 1].month,
                                      true,
                                  )}`
                                : ''}
                        </span>
                        <span>{t(`admin.analytics.${population}`)}</span>
                    </div>
                    <svg
                        className="analytics-chart"
                        viewBox="0 0 600 235"
                        aria-hidden="true"
                    >
                        {Array.from({ length: 5 }, (_, index) => {
                            const y = top + height - (index * height) / 4;
                            return (
                                <g key={index}>
                                    <line
                                        x1={left}
                                        x2={left + width}
                                        y1={y}
                                        y2={y}
                                        className="analytics-chart-grid"
                                    />
                                    <text
                                        x={left - 6}
                                        y={y + 4}
                                        textAnchor="end"
                                    >
                                        {number((index * ceiling) / 4)}
                                    </text>
                                </g>
                            );
                        })}
                        {months.map((row, index) => {
                            const counts = row.counts;
                            const x = left + slot * (index + 0.5);
                            let bottom = top + height;
                            const title =
                                row.counts && row.capturedAt
                                    ? `${monthName(row.month, true)}: ${number(
                                          row.total ?? 0,
                                      )}. ${visibleStatuses
                                          .map(
                                              status =>
                                                  `${t(
                                                      `admin.analytics.mainContactChart_${status.key}`,
                                                  )}: ${number(
                                                      row.counts?.[
                                                          status.key
                                                      ] ?? 0,
                                                  )}`,
                                          )
                                          .join(', ')}. ${t(
                                          'admin.analytics.snapshotTaken',
                                          {
                                              date: new Date(
                                                  row.capturedAt,
                                              ).toLocaleDateString(
                                                  i18n.language,
                                                  { timeZone: data.timeZone },
                                              ),
                                          },
                                      )}`
                                    : t(
                                          'admin.analytics.coverageHistoryUnavailable',
                                      );
                            return (
                                <g key={row.month}>
                                    <title>{title}</title>
                                    {counts &&
                                        visibleStatuses.map(status => {
                                            const barHeight =
                                                (counts[status.key] / ceiling) *
                                                height;
                                            bottom -= barHeight;
                                            return (
                                                <rect
                                                    key={status.key}
                                                    x={x - 12}
                                                    y={bottom}
                                                    width={24}
                                                    height={barHeight}
                                                    fill={status.color}
                                                    data-month={row.month}
                                                    data-status={status.key}
                                                />
                                            );
                                        })}
                                    <text
                                        x={x}
                                        y={
                                            row.counts
                                                ? top +
                                                  height -
                                                  ((row.total ?? 0) / ceiling) *
                                                      height -
                                                  7
                                                : top + height - 7
                                        }
                                        textAnchor="middle"
                                        className="analytics-chart-count"
                                    >
                                        {row.counts
                                            ? `${number(row.total ?? 0)}${
                                                  row.partial ? '*' : ''
                                              }`
                                            : '—'}
                                    </text>
                                    <text
                                        x={x}
                                        y={top + height + 24}
                                        textAnchor="middle"
                                    >
                                        {monthName(row.month)}
                                    </text>
                                </g>
                            );
                        })}
                    </svg>
                    <div className="analytics-history-key" aria-hidden="true">
                        {visibleStatuses.map(status => (
                            <span key={status.key}>
                                <i style={{ backgroundColor: status.color }} />
                                {t(
                                    `admin.analytics.mainContactChart_${status.key}`,
                                )}
                            </span>
                        ))}
                    </div>
                    <p className="analytics-note">
                        {t('admin.analytics.coverageHistoryPartial')}
                    </p>
                    <div className="sr-only">
                        <table>
                            <caption>
                                {t(
                                    employee
                                        ? 'admin.analytics.employeeCoverageTitle'
                                        : 'admin.analytics.coverageHistoryTitle',
                                )}
                            </caption>
                            <thead>
                                <tr>
                                    <th>
                                        {t('admin.analytics.reportingMonth')}
                                    </th>
                                    <th>
                                        {t(`admin.analytics.${population}`)}
                                    </th>
                                    {visibleStatuses.map(status => (
                                        <th key={status.key}>
                                            {t(
                                                `admin.analytics.mainContactChart_${status.key}`,
                                            )}
                                        </th>
                                    ))}
                                    <th>
                                        {t(
                                            'admin.analytics.coverageHistorySnapshot',
                                        )}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {months.map(row => (
                                    <tr key={row.month}>
                                        <th scope="row">
                                            {monthName(row.month, true)}
                                        </th>
                                        <td>
                                            {row.counts
                                                ? number(row.total ?? 0)
                                                : t(
                                                      'admin.analytics.coverageHistoryUnavailable',
                                                  )}
                                        </td>
                                        {visibleStatuses.map(status => (
                                            <td key={status.key}>
                                                {row.counts
                                                    ? number(
                                                          row.counts[
                                                              status.key
                                                          ],
                                                      )
                                                    : '—'}
                                            </td>
                                        ))}
                                        <td>
                                            {row.capturedAt
                                                ? new Date(
                                                      row.capturedAt,
                                                  ).toLocaleDateString(
                                                      i18n.language,
                                                      {
                                                          timeZone:
                                                              data.timeZone,
                                                      },
                                                  )
                                                : '—'}
                                            {row.partial
                                                ? ` · ${t(
                                                      'admin.analytics.coverageHistoryPartialLabel',
                                                  )}`
                                                : ''}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </AnalyticsChartFrame>
    );
};
